import type { TOC } from '@ember/component/template-only';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import EmbeddedApp from 'garaje/components/embedded-app';
import UiClassicModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type { AddRuleModalData } from 'garaje/pods/location-overview/communications/settings/controller';

interface EmnoGlobalSettingsAddRuleModalSignature {
  Element: HTMLDivElement;
  Args: {
    /** Controlled open state, driven by the pod controller. */
    isOpen: boolean;
    /** Fired when the shell should close (backdrop click / Escape). The iframe
     *  itself closes via a `closeModal` message the controller also handles. */
    onClose: () => void;
    /** URL of the emno-fe modal-content route (see url-builder.ts →
     *  `emno.embeddedGlobalSettingsAddRuleModalUrl`). */
    modalIframeUrl: string;
    /** Section payload from the outer emno-fe iframe. Forwarded to the modal
     *  iframe as `<EmbeddedApp @context>`, which EmbeddedApp posts across the
     *  iframe-resizer bridge after the `ready` handshake. */
    modalData: AddRuleModalData | Record<string, never>;
  };
}

/**
 * Modal shell for the emno-fe "Add rule" flow. Body is a nested emno-fe iframe
 * hosting app/global-overview/settings/add-rule/page.tsx. The pod controller
 * listens to the shared messageBus for `emnoGlobalSettingsAddRule` (to open),
 * `closeModal` (to shut), and `showToaster` (post-save feedback).
 */
export default <template>
  {{#if @isOpen}}
    <UiClassicModalDialog @onClose={{@onClose}} @translucentOverlay={{true}} @containerClass="!max-w-form">
      <FocusTrapper>
        {{! -mx-8 (not -m-8) to match the other modal EmbeddedApps: .ember-modal-dialog has no
            padding to cancel vertically, so a negative block margin just pushes the iframe's
            painted box 32px past the dialog's content box — permanent dead space you can only
            reach by scrolling the modal. Horizontal bleed is still wanted; the framed page's own
            side gutter lines its content up with the dialog edges. }}
        <EmbeddedApp
          @id="emno-global-settings-add-rule-modal"
          @url={{@modalIframeUrl}}
          @context={{@modalData}}
          class="-mx-8"
          data-test-emno-global-settings-add-rule-modal-iframe
        />
      </FocusTrapper>
    </UiClassicModalDialog>
  {{/if}}
</template> satisfies TOC<EmnoGlobalSettingsAddRuleModalSignature>;
